import { Wifi, WifiOff, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { SyncState } from '@/hooks/useSync'

interface SyncIndicatorProps {
  isOnline: boolean
  syncState: SyncState
  onSync: () => void
  className?: string
  compact?: boolean
}

export function SyncIndicator({
  isOnline,
  syncState,
  onSync,
  className,
  compact = false,
}: SyncIndicatorProps) {
  const { isSyncing, pendingCount, lastSyncAt, syncErrors } = syncState

  if (compact) {
    return (
      <div className={cn('flex items-center gap-1.5', className)}>
        {isOnline ? (
          <Wifi className="h-4 w-4 text-green-600" />
        ) : (
          <WifiOff className="h-4 w-4 text-orange-500" />
        )}
        {pendingCount > 0 && (
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white">
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        isOnline ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50',
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {isOnline ? (
            <Wifi className="h-4 w-4 text-green-600" />
          ) : (
            <WifiOff className="h-4 w-4 text-orange-600" />
          )}
          <span
            className={cn(
              'text-sm font-medium',
              isOnline ? 'text-green-800' : 'text-orange-800'
            )}
          >
            {isOnline ? 'En línea' : 'Sin conexión'}
          </span>
        </div>

        {isOnline && pendingCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onSync}
            disabled={isSyncing}
            className="h-7 px-2 text-xs"
          >
            <RefreshCw
              className={cn('h-3 w-3 mr-1', isSyncing && 'animate-spin')}
            />
            {isSyncing ? 'Sincronizando...' : `Sincronizar (${pendingCount})`}
          </Button>
        )}
      </div>

      {pendingCount > 0 && !isSyncing && (
        <p className="mt-1 text-xs text-orange-700">
          {pendingCount} registro{pendingCount !== 1 ? 's' : ''} pendiente
          {pendingCount !== 1 ? 's' : ''} de sincronizar
        </p>
      )}

      {isSyncing && (
        <p className="mt-1 text-xs text-green-700">Sincronizando registros...</p>
      )}

      {!isSyncing && pendingCount === 0 && lastSyncAt && (
        <div className="mt-1 flex items-center gap-1">
          <CheckCircle className="h-3 w-3 text-green-600" />
          <p className="text-xs text-green-700">
            Última sincronización: {lastSyncAt.toLocaleTimeString('es-CL')}
          </p>
        </div>
      )}

      {syncErrors.length > 0 && (
        <div className="mt-2 space-y-1">
          {syncErrors.map((error, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertCircle className="h-3 w-3 mt-0.5 text-red-500 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-red-600">{error.recordLabel}</p>
                <p className="text-xs text-red-600">{error.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
